/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react"

import { useDispatch } from "react-redux"

import type { IAppDispatch } from "@/3514/store"
import type { TEmptyCallback } from "@/shared/types/functions"

import {
    type TCoachDashboardQueryPeriod as TPeriod,
    type TCoachDashboardQueryOrder as TQueryOrder
} from "./coach.dashboard.api.types"
import { coachDashboardReducerActions as actions } from "./coach.dashboard.store.slice"
import {
    ECoachDashboardActiveTab as EActiveTab,
    type TCoachDashboardTableSortConfig as TSortConfig
} from "./coach.dashboard.ui.types"

type TCoachDashboardDispatcher = {
    changeTab(tab: EActiveTab): void
    setSearchValue(value: string): void
    setPaginationPage(page: number): void
    changeQueryPeriod(order: TPeriod): void
    changeQueryOrder(order: TQueryOrder): void
    changeSortConfig(sort: TSortConfig): void
    reset: TEmptyCallback
}

function useCoachDashboardDispatcher(): ReturnType<() => TCoachDashboardDispatcher> {
    const dispatch: IAppDispatch = useDispatch<IAppDispatch>()

    const changeTab: (tab: EActiveTab) => void = useCallback(
        (tab: EActiveTab): void => void dispatch(actions.changeTab(tab)),
        []
    )

    const setSearchValue: (value: string) => void = useCallback(
        (value: string): void => void dispatch(actions.setSearchValue(value)),
        []
    )

    const setPaginationPage: (page: number) => void = useCallback(
        (page: number): void => void dispatch(actions.setPaginationPage(page)),
        []
    )

    const changeQueryPeriod: (period: TPeriod) => void = useCallback(
        (period: TPeriod): void => void dispatch(actions.changeQueryPeriod(period)),
        []
    )

    const changeQueryOrder: (order: TQueryOrder) => void = useCallback(
        (order: TQueryOrder): void => void dispatch(actions.changeQueryOrder(order)),
        []
    )

    const changeSortConfig: (sort: TSortConfig) => void = useCallback(
        (sort: TSortConfig): void => void dispatch(actions.changeSortConfig(sort)),
        []
    )

    const resetState: TEmptyCallback = useCallback((): void => void dispatch(actions.reset(void 0)), [])

    return useMemo(
        (): TCoachDashboardDispatcher => ({
            changeTab,
            setSearchValue,
            setPaginationPage,
            changeQueryPeriod,
            changeQueryOrder,
            changeSortConfig,
            reset: resetState
        }),
        [dispatch]
    )
}

export { useCoachDashboardDispatcher, type TCoachDashboardDispatcher }
